import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import {
  zapAtomFamily_SUSPENDS,
  zapMetadataAtomFamily_SUSPENDS,
} from "../zaps";
import { AgentFetchResult, AssetType, ChatbotFetchResult } from "../types";
import { isZapErrorObject } from "../utils";
import { fetchChatbotAtomFamily_SUSPENDS } from "./fetchChatbotAtomFamily_SUSPENDS";
import { fetchInterfacePageAtomFamily_SUSPENDS } from "./fetchInterfacePageAtomFamily_SUSPENDS";
import { fetchInterfaceProjectAtomFamily_SUSPENDS } from "./fetchInterfaceProjectAtomFamily_SUSPENDS";
import { tableAtomFamily_SUSPENDS } from "./tableAtomFamily_SUSPENDS";
import { fetchAgentAtomFamily_SUSPENDS } from "./fetchAgentAtomFamily_SUSPENDS";

type AssetParams = {
  assetType: AssetType;
  assetId: string | null;
  interfaceProjectId: string | undefined;
};

/**
 * Fetches asset data but does not distinguish between 403 (forbidden) and 404 (not found).
 * Returns null for both cases, except tables which return metadata even for 403.
 * Use assetAccessAtomFamily_SUSPENDS if you need to differentiate between permission
 * errors and missing assets.
 */
export const assetAtomFamily_SUSPENDS = atomFamily(
  ({ assetType, assetId, interfaceProjectId }: AssetParams) => {
    const baseAtom = atom(async (get) => {
      if (assetType === "zap" && assetId) {
        const [zapMetadataResult, zapResult] = await Promise.allSettled([
          get(zapMetadataAtomFamily_SUSPENDS(assetId)),
          get(zapAtomFamily_SUSPENDS(assetId)),
        ]);

        const zapMetadata =
          zapMetadataResult?.status === "fulfilled"
            ? zapMetadataResult.value
            : null;
        const zapData =
          zapResult?.status === "fulfilled" ? zapResult.value : null;

        // zapMetadata gives error for 404 only, not 403
        // zapData gives error for both 403 and 404
        // Success means asset is found, and you have access (no 403 or 404)
        const isSuccess =
          !isZapErrorObject(zapMetadata) && !isZapErrorObject(zapData);
        return isSuccess ? zapMetadata : null;
      }

      if (assetType === "table" && assetId) {
        const tableResult = await get(tableAtomFamily_SUSPENDS(assetId));
        const isSuccess = tableResult?.type === "success";
        return isSuccess ? tableResult.data : null;
      }

      if (assetType === "chatbot" && assetId) {
        const chatbotResult = (await get(
          fetchChatbotAtomFamily_SUSPENDS(assetId),
        )) as ChatbotFetchResult | null | undefined;

        const isSuccess = chatbotResult?.type === "success";
        return isSuccess ? chatbotResult.data : null;
      }

      // assetId for interfaces will be the pageId
      if (assetType === "interface") {
        if (assetId) {
          const pageResult = await get(
            fetchInterfacePageAtomFamily_SUSPENDS({
              interfacePageId: assetId,
              interfaceProjectId,
            }),
          );
          return pageResult?.type === "success" ? pageResult.data : null;
        }

        if (interfaceProjectId) {
          const projResult = await get(
            fetchInterfaceProjectAtomFamily_SUSPENDS(interfaceProjectId),
          );
          return projResult?.type === "success" ? projResult.data : null;
        }
      }

      if (assetType === "agent" && assetId) {
        const agentResult = (await get(
          fetchAgentAtomFamily_SUSPENDS(assetId),
        )) as AgentFetchResult | null | undefined;

        const isSuccess = agentResult?.type === "success";
        return isSuccess ? agentResult.data : null;
      }

      return null;
    });

    baseAtom.debugLabel = `assetAtomFamily_SUSPENDS(${JSON.stringify({ assetType, assetId, interfaceProjectId })})`;

    return baseAtom;
  },
  (a, b) =>
    a.assetType === b.assetType &&
    a.assetId === b.assetId &&
    a.interfaceProjectId === b.interfaceProjectId,
);
